import { useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import {
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'

import { observer } from 'mobx-react-lite'

import userStore from 'entities/user/module/user-store.ts'

import { BuyTicketApi } from 'shared/api/BuyTicket.ts'
import { DetailsAppeal } from 'shared/api/BuyTicketModel.ts'
import { MyTicketItem } from 'shared/api/MyTicketsModel.ts'
import ErrorImage from 'shared/images/error.png'
import { getErrorMessage } from 'shared/lib/errorText.ts'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib/useDialogBottomSheet.ts'
import { Loader } from 'shared/ui/loader'
import { PopupContent } from 'shared/ui/poup-content/poup-content.tsx'

import {
  AdditionalServicePage,
  AppealTicket,
  DetailPassengers,
  ListPassengers,
  ReturnRefund,
} from './router/index.ts'
import styles from './styles.module.scss'

export const RETURN_REFUND = {
  EXCHANGE: 'exchange',
  REFUND: 'refund',
  SERVICE: 'service',
}

const MyTicketNew = observer(() => {
  const { t } = useTranslation()

  const navigate = useNavigate()

  const { id, appealId } = useParams()
  const [searchParams] = useSearchParams()

  const { openDialog, closeDialog } = useDialogBottomSheet()

  const userData = userStore.getAccountInfo()

  const isArchive = !!searchParams.get('type')

  const loading = useRef<boolean>(false)
  const [ticket, setTicket] = useState<MyTicketItem>()
  const [appealDetails, setAppealDetails] = useState<DetailsAppeal[] | null>(
    null,
  )

  const getTicket = async () => {
    loading.current = true
    try {
      if (id) {
        const { data: ticketDetail } = await BuyTicketApi.getDetailTicket(id)
        setTicket(ticketDetail.data)
      }
    } catch (err) {
      const errorText = getErrorMessage(err)

      onShowPopUp(errorText)
    } finally {
      loading.current = false
    }
  }

  const onShowPopUp = (text?: string) => {
    openDialog({
      child: (
        <PopupContent
          image={ErrorImage}
          title={t('error')}
          description={text || t('please_tey_again')}
          button={t('close')}
          onClick={() => {
            closeDialog()
            navigate(-1)
          }}
        />
      ),
      isDialog: false,
    })
  }

  const getAppealDetail = async () => {
    if (appealId) {
      try {
        const { data } = await BuyTicketApi.getAppealInfo(String(appealId))
        setAppealDetails(data.data)
      } catch (error: unknown) {}
    }
  }

  useEffect(() => {
    if (userData && !ticket && !loading.current) {
      getTicket()
      getAppealDetail()
    }
  }, [userData, ticket, loading.current])

  if (!ticket) {
    return (
      <div className={styles.loaderWrapper}>
        <Loader />
      </div>
    )
  }

  return (
    <Routes>
      <Route
        path={'/'}
        element={
          <ListPassengers
            ticket={ticket}
            isArchiveTicket={isArchive}
            additionalService={appealDetails || undefined}
            onTicketRefresh={getTicket}
          />
        }
      />

      <Route
        path={'/detail/:passenger'}
        element={
          <DetailPassengers
            ticket={ticket}
            additionalService={appealDetails || undefined}
          />
        }
      />

      <Route
        path={`/appeal`}
        element={<ReturnRefund ticket={ticket} />}
      />

      {
        <Route
          path={`/${RETURN_REFUND.SERVICE}`}
          element={<AdditionalServicePage ticket={ticket} />}
        />
      }

      <Route
        path={'/appeal_ticket'}
        element={<AppealTicket />}
      />
    </Routes>
  )
})

export default MyTicketNew
